import { Client, Estimate, Facility, FacilityLevelOfCareWithCrossoverDays, Payor, PayorPlan, Plan, QuoteMethod, RateAsPayorMap } from '@finpay-estimation/estimation-types';
import { AppDispatch } from '../../shared/state/store';
import { estimatorSlice } from '@finpay-development/shared-platform/lib/esm/shared/state/estimator-slice';
import { PayerRow } from '@finpay-development/shared-platform/lib/esm/admin-configuration/models/payers';
import { Vob } from '@finpay-estimation/estimation-types';
import { admissionsAdvisorService } from '@finpay-development/shared-platform/lib/esm/admissions-advisor/services/admissions-advisor-services';
import { showErrorStatus } from '@finpay-development/shared-platform/lib/esm/security/state/user-slice';
import { loadSavedEstimatesListv3, saveSelectedEstimate, saveSelectedEstimateSelfPay } from '@finpay-development/shared-platform/lib/esm/shared/state/estimator-thunk';
import _ from 'lodash';

// Define interfaces for the facility payer data structure
// TODO: use vob types
interface FacilityPayorPlan {
  cfgPayorPlanId: number;
  facilityPayorPlanId: number;
  facilityPayorPlanName: string;
  facilityPayorPlanCode?: string;
}

// TODO: use vob types
interface FacilityPayer {
  cfgPayorId: number;
  facilityPayorId: number;
  facilityPayorName: string;
  facilityPayorCode?: string;
  inNetwork: boolean;
  facilityPayorPlans?: FacilityPayorPlan[];
}

type VobWithRateAs = Vob & { rateAsPayorPlan?: PayorPlan };


type FacilityPayerLike = {
  cfgPayorId: number;
  facilityPayorId?: number;
  facilityPayorName?: string;
  inNetwork?: boolean;
  facilityPayorPlans?: {
    cfgPayorPlanId: number;
    facilityPayorPlanId: number;
    facilityPayorPlanName?: string;
  }[];
};


export function isFacilityPlanIdOnFacility(
  facilityPayers: FacilityPayerLike[] | null | undefined,
  facilityPlanId: number | null | undefined
): boolean {
  if (facilityPlanId == null || !facilityPayers?.length) return false;
  const target = Number(facilityPlanId);
  return facilityPayers.some((payer) =>
    (payer.facilityPayorPlans ?? []).some((plan) => Number(plan.facilityPayorPlanId) === target)
  );
}


export function resolvePriorCareLosDaysAfterRematch(params: {
  loc: { losDays?: number; facilityPayorPlanId?: number | string | null; facilityPlanId?: number | string | null };
  found: { losDays?: number; recommendedLos?: number };
  quoteMethod?: QuoteMethod;
  previousFacilityPlanId?: number | string | null;
  newFacilityPlanId?: number | string | null;
}): number | undefined {
  const { loc, found, quoteMethod, previousFacilityPlanId, newFacilityPlanId } = params;
  const locPlanId = loc.facilityPayorPlanId ?? loc.facilityPlanId ?? previousFacilityPlanId;
  const rateAsPlanChanged =
    locPlanId != null &&
    newFacilityPlanId != null &&
    Number(locPlanId) !== Number(newFacilityPlanId);

  if (!rateAsPlanChanged) {
    return loc.losDays ?? found.losDays ?? found.recommendedLos;
  }

  switch (quoteMethod) {
    case QuoteMethod.MANUAL:
    case QuoteMethod.SCA:
      return loc.losDays ?? found.losDays ?? found.recommendedLos;
    case QuoteMethod.RLOS:
      return found.recommendedLos ?? found.losDays ?? loc.losDays;
    case QuoteMethod.AVGLOS:
    default:
      return found.losDays ?? found.recommendedLos ?? loc.losDays;
  }
}

export function rematchRateAsPayorPlanToFacility(
  rateAs: { payor?: Payor | null; plan?: Plan | null; isOverridden?: boolean } | null | undefined,
  facilityPayers: FacilityPayerLike[] | null | undefined,
  masterPayors?: Map<number, { payorName?: string; payorPlans?: { payorPlanId: number; planName?: string }[] }> | null
): PayorPlan | null {
  if (!rateAs?.payor?.payorId || !rateAs?.plan?.payorPlanId || !facilityPayers?.length) {
    return null;
  }
  const payorId = Number(rateAs.payor.payorId);
  const planId = Number(rateAs.plan.payorPlanId);
  for (const payer of facilityPayers) {
    if (Number(payer.cfgPayorId) !== payorId) continue;
    const plan = (payer.facilityPayorPlans ?? []).find(
      (p) => Number(p.cfgPayorPlanId) === planId
    );
    if (!plan) continue;
    const matchedMasterPayor = masterPayors?.get(payer.cfgPayorId);
    const matchedMasterPlan = matchedMasterPayor?.payorPlans?.find(
      (p: any) => Number(p.payorPlanId) === Number(plan.cfgPayorPlanId)
    );
    return {
      payor: {
        payorId: payer.cfgPayorId,
        ...(payer.facilityPayorId != null && { facilityPayorId: payer.facilityPayorId }),
        payorName: matchedMasterPayor?.payorName || rateAs.payor.payorName || payer.facilityPayorName,
        facilityPayorName: payer.facilityPayorName,
        inNetwork: payer.inNetwork,
      },
      plan: {
        payorPlanId: plan.cfgPayorPlanId,
        facilityPlanId: plan.facilityPayorPlanId,
        planName: matchedMasterPlan?.planName || rateAs.plan.planName || plan.facilityPayorPlanName,
        facilityPlanName: plan.facilityPayorPlanName,
      },
      isOverridden: rateAs.isOverridden === true,
    } as PayorPlan;
  }
  return null;
}

export function getOverriddenRateAsPayorPlan(
  vob?: Vob | null,
  estimate?: Estimate | null
): PayorPlan | null {
  const vobRateAs = (vob as VobWithRateAs | undefined)?.rateAsPayorPlan;
  if (vobRateAs?.isOverridden === true && vobRateAs.payor && vobRateAs.plan) {
    return vobRateAs as PayorPlan;
  }
  const estRateAs = estimate?.rateAsPayorPlan as PayorPlan | undefined;
  if (estRateAs?.isOverridden === true && estRateAs.payor && estRateAs.plan) {
    return estRateAs;
  }
  return null;
}

export function resolvePayorPlanForRateAsLoad(
  vob?: Vob | null,
  estimate?: Estimate | null
): { payor: Payor; plan: Plan } | null {
  const overridden = getOverriddenRateAsPayorPlan(vob, estimate);
  if (overridden) {
    return { payor: overridden.payor, plan: overridden.plan };
  }
  if (vob?.payor && vob?.plan) {
    return { payor: vob.payor, plan: vob.plan };
  }
  return null;
}

/**
 * Loads facility payers and sets the rateAsPayorPlan based on VOB data
 * 
 * @param client Client information
 * @param facility Facility information
 * @param dispatch Redux dispatch function
 * @param masterPayors Map of master payors
 * @param payor Payor information
 * @param plan Plan information
 * @param rateAsPayorMap Map of rate as payor information
 * @returns Promise that resolves when the operation is complete
 */
export const loadAndSetRateAsPayorPlan = async (
  client: Client,
  facility: Facility,
  dispatch: AppDispatch,
  masterPayors: Map<number, PayerRow>,
  payor: Payor,
  plan: Plan,
  rateAsPayorMap: Record<number, RateAsPayorMap[]>,
  estimate?: Estimate,
  rateAsPayorPlan?: PayorPlan,
  selectedVob?: Vob | undefined,
  updatedEstimate?: Estimate,
  newVobId?: string | number,
  preferProvidedPayorPlan?: boolean,
): Promise<boolean> => {
  if (!facility?.facilityId) return false;

  try {
    const response = await admissionsAdvisorService.getEstimatorFacilityLocPayers(
      -2,
      Number(facility.facilityId)
    );

    if (!response.hasErrors && response.entity?.facilityPayors) {
      return await findAndSetRateAsPayorPlan({
        facilityPayers: response.entity.facilityPayors,
        client,
        facility,
        payor,
        plan,
        dispatch,
        rateAsPayorMap,
        masterPayors,
        estimate,
        rateAsPayorPlan,
        selectedVob,
        updatedEstimate,
        newVobId,
        preferProvidedPayorPlan,
      });
    }
  } catch (error) {
    console.error('Error in loadAndSetRateAsPayorPlan:', error);
  }
  return false;
};



/**
 * Finds and sets the rateAsPayorPlan based on VOB data and facility payers
 * 
 * @param params Configuration object
 * @param params.facilityPayers List of facility payers
 * @param params.vob VOB data containing payor and plan information
 * @param params.dispatch Redux dispatch function
 * @returns The found PayorPlan object or null if no match was found
 */
const findAndSetRateAsPayorPlan = async (params: {
  facilityPayers: FacilityPayer[];
  client: Client;
  facility: Facility;
  payor: Payor;
  plan: Plan;
  dispatch: AppDispatch;
  rateAsPayorMap: Record<number, RateAsPayorMap[]>;
  masterPayors: Map<number, PayerRow>;
  estimate?: Estimate;
  rateAsPayorPlan?: PayorPlan;
  selectedVob?: Vob | undefined;
  updatedEstimate?: Estimate;
  newVobId?: string | number,
  preferProvidedPayorPlan?: boolean,
}): Promise<boolean> => {

  console.log(
    "%cFinding and Setting Rate as Payor Plan",
    "color: #4CAF50; font-weight: bold; font-size: 12px; background: #E8F5E9; padding: 4px 8px; border-radius: 4px;"
  );
  const { facilityPayers, client, facility, payor, plan, dispatch, rateAsPayorMap, masterPayors, estimate, rateAsPayorPlan, selectedVob, updatedEstimate, newVobId, preferProvidedPayorPlan } = params;
  let payorResult: PayorPlan | null = null;

  if (!client || !facility || !payor || !plan) {
    return false;
  }

  try {
    const comparisonFacilityId = estimate?.facilityId ?? selectedVob?.facility?.facilityId;
    const changingFromFacility =
      comparisonFacilityId != null &&
      facility?.facilityId != null &&
      Number(comparisonFacilityId) !== Number(facility.facilityId);

    // Align with "Levels of Care -" display: when Private Pay is checked use old logic; otherwise if selectedVob.rateAsPayorPlan.isOverridden use selectedVob's value.
    // On a facility change, ignore the estimate's saved Rate as. It belongs to the previous facility and would rematch (for example Average Out-of-Network) onto the VOB just selected.
    const isPrivatePayContext =
      updatedEstimate != null
        ? !!updatedEstimate?.isPrivatePay
        : !!estimate?.isPrivatePay;
    if (
      !preferProvidedPayorPlan &&
      selectedVob?.rateAsPayorPlan?.isOverridden === true &&
      selectedVob.rateAsPayorPlan?.payor &&
      selectedVob.rateAsPayorPlan?.plan &&
      !isPrivatePayContext
    ) {
      payorResult = selectedVob.rateAsPayorPlan as PayorPlan;
    }

    if (
      !changingFromFacility &&
      !preferProvidedPayorPlan &&
      !payorResult &&
      estimate?.rateAsPayorPlan?.isOverridden === true &&
      (estimate.rateAsPayorPlan as PayorPlan)?.payor &&
      (estimate.rateAsPayorPlan as PayorPlan)?.plan &&
      !isPrivatePayContext
    ) {
      payorResult = estimate.rateAsPayorPlan as PayorPlan;
    }

    if (payorResult?.plan?.facilityPlanId != null && !isFacilityPlanIdOnFacility(facilityPayers, payorResult.plan.facilityPlanId)) {
      const rematched = rematchRateAsPayorPlanToFacility(payorResult, facilityPayers, masterPayors);
      payorResult = rematched
        ? ({ ...rematched, isOverridden: payorResult.isOverridden === true } as PayorPlan)
        : null;
    }

    if (preferProvidedPayorPlan && !payorResult && plan?.facilityPlanId != null) {
      const facilityPlanId = Number(plan.facilityPlanId);
      if (isFacilityPlanIdOnFacility(facilityPayers, facilityPlanId)) {
        const matchedFacilityPayor = facilityPayers.find((p) =>
          p.facilityPayorPlans?.some((pl) => Number(pl.facilityPayorPlanId) === facilityPlanId)
        );
        const matchedFacilityPlan = matchedFacilityPayor?.facilityPayorPlans?.find(
          (pl) => Number(pl.facilityPayorPlanId) === facilityPlanId
        );
        if (matchedFacilityPayor && matchedFacilityPlan) {
          const matchedMasterPayor = masterPayors.get(matchedFacilityPayor.cfgPayorId);
          const matchedMasterPlan = matchedMasterPayor?.payorPlans?.find(
            (p: any) => Number(p.payorPlanId) === Number(matchedFacilityPlan.cfgPayorPlanId)
          );
          payorResult = {
            payor: {
              payorId: matchedFacilityPayor.cfgPayorId,
              facilityPayorId: matchedFacilityPayor.facilityPayorId,
              payorName: matchedMasterPayor?.payorName || payor.payorName || matchedFacilityPayor.facilityPayorName,
              facilityPayorName: matchedFacilityPayor.facilityPayorName,
              inNetwork: matchedFacilityPayor.inNetwork,
            },
            plan: {
              payorPlanId: matchedFacilityPlan.cfgPayorPlanId,
              facilityPlanId: matchedFacilityPlan.facilityPayorPlanId,
              planName: matchedMasterPlan?.planName || plan.planName || matchedFacilityPlan.facilityPayorPlanName,
              facilityPlanName: matchedFacilityPlan.facilityPayorPlanName,
            },
            isOverridden: true,
          };
          if (!payorResult.payor.facilityPayorName) delete payorResult.payor.facilityPayorName;
          if (!payorResult.plan.facilityPlanName) delete payorResult.plan.facilityPlanName;
        }
      } else {
        const rematched = rematchRateAsPayorPlanToFacility(
          { payor, plan, isOverridden: true } as PayorPlan,
          facilityPayers,
          masterPayors
        );
        if (rematched) {
          payorResult = { ...rematched, isOverridden: true } as PayorPlan;
        }
      }
    }

    if (!payorResult) {
      // Match first if payor & plan provided
      if (payor?.payorId && plan?.payorPlanId) {
        const clientRateAsPayorMap = rateAsPayorMap[client.clientId];

        const payorPlan = findMatchedPayorPlan(
          facilityPayers,
          payor,
          plan,
          clientRateAsPayorMap,
          masterPayors
        );

        if (payorPlan) {
          payorResult = preferProvidedPayorPlan
            ? { ...payorPlan, isOverridden: true }
            : payorPlan;

          // Clean up empty properties
          if (!payorResult.payor.facilityPayorName)
            delete payorResult.payor.facilityPayorName;
          if (!payorResult.plan.facilityPlanName)
            delete payorResult.plan.facilityPlanName;
        }
      }

      // Fallback to Average Out-of-Network
      if (!payorResult) {
      const averageOutOfNetworkPayor = findAverageOutOfNetworkPayor(facilityPayers, masterPayors);

      if (
        averageOutOfNetworkPayor?.facilityPayorPlans?.length
      ) {
        const firstPlan = averageOutOfNetworkPayor.facilityPayorPlans[0];
        const matchedMasterPayor = masterPayors.get(averageOutOfNetworkPayor.cfgPayorId);
        const matchedMasterPlan = matchedMasterPayor?.payorPlans?.find(
          (p: any) => p.payorPlanId === firstPlan.cfgPayorPlanId
        );

        payorResult = {
          payor: {
            payorId: averageOutOfNetworkPayor.cfgPayorId,
            facilityPayorId: averageOutOfNetworkPayor.facilityPayorId,
            payorName: matchedMasterPayor?.payorName,
            facilityPayorName: averageOutOfNetworkPayor.facilityPayorName,
            inNetwork: averageOutOfNetworkPayor.inNetwork
          },
          plan: {
            payorPlanId: firstPlan.cfgPayorPlanId,
            facilityPlanId: firstPlan.facilityPayorPlanId,
            planName: matchedMasterPlan?.planName,
            facilityPlanName: firstPlan.facilityPayorPlanName
          },
          isOverridden: false
        };

        if (!averageOutOfNetworkPayor.facilityPayorName)
          delete payorResult.payor.facilityPayorName;
        if (!firstPlan.facilityPayorPlanName)
          delete payorResult.plan.facilityPlanName;
      }
    }
    }

    let levelsOfCareResponse;
    if (payorResult?.plan?.facilityPlanId) {
      levelsOfCareResponse = await admissionsAdvisorService.getFacilityLevelsOfCare(
        facility?.facilityId!,
        payorResult.plan.facilityPlanId
      );

      if (!levelsOfCareResponse.hasErrors && Array.isArray(levelsOfCareResponse.entity)) {
        const formedLevels = levelsOfCareResponse.entity
        
          .map((level: any) => ({
            ...level,
            levelOfCareId: level.facilityLevelOfCareId,
            levelOfCareCode: level.facilityLevelOfCareCode,
            levelOfCareName: level.facilityLevelOfCareName,
            vobClassificationId: level.vobClassificationId,
            sortOrder: level.sortOrder || 0
          }));
        dispatch(estimatorSlice.actions.setFacilityLevelsOfCareList(formedLevels));
      } else {
        // API call failed or returned errors
        console.error('Failed to load facility levels of care:', levelsOfCareResponse);
        if (dispatch) {
          dispatch(showErrorStatus('Failed to load levels of care. Please try again.')); // check with RD on relevant error message
          dispatch(estimatorSlice.actions.setFacilityLevelsOfCareList(null));
        }
      }
    } else if (!payorResult) {
      if (dispatch) {
        dispatch(estimatorSlice.actions.setFacilityLevelsOfCareList(null));
      }
    }
    dispatch(estimatorSlice.actions.setRateAsPayorPlan(payorResult));

    // the rateAsPayorPlan is always Average OON
    const changingFromPrivatePay = (estimate?.isPrivatePay && (updatedEstimate?.isPrivatePay !== estimate.isPrivatePay));
    const selectedVobId = newVobId;

    // Skip handleLOCMatching when VOB has overridden Rate as and it matches estimatorContext rateAsPayorPlan (same payorId/payorPlanId)
    const vobRateAs = selectedVob?.rateAsPayorPlan as { isOverridden?: boolean; payorId?: number; payorPlanId?: number; payor?: { payorId?: number }; plan?: { payorPlanId?: number; facilityPlanId?: number } } | undefined;
    const vobPayorId = vobRateAs?.payorId ?? vobRateAs?.payor?.payorId;
    const vobPayorPlanId = vobRateAs?.payorPlanId ?? vobRateAs?.plan?.payorPlanId;
    const reduxPayorId = rateAsPayorPlan?.payor?.payorId;
    const reduxPayorPlanId = rateAsPayorPlan?.plan?.payorPlanId;
    const skipBecauseVobOverriddenMatchesRedux =
      !preferProvidedPayorPlan &&
      vobRateAs?.isOverridden === true &&
      vobPayorId != null &&
      vobPayorPlanId != null &&
      vobPayorId === reduxPayorId &&
      vobPayorPlanId === reduxPayorPlanId;

    const payorFacilityPlanId = payorResult?.plan?.facilityPlanId;
    const hasIncompatibleLocs = Boolean(
      payorFacilityPlanId != null &&
      estimate?.facilityLevelOfCare?.some((loc: any) => {
        const locPlanId = loc.facilityPayorPlanId ?? loc.facilityPlanId;
        return locPlanId != null && Number(locPlanId) !== Number(payorFacilityPlanId);
      })
    );

    // Sync estimate LOCs to current plan when we have LOCs + payorResult. Always run when Private Pay checkbox changes (check or uncheck), or the facility changed, even if VOB override matches Redux.
    const privatePayToggled =
      estimate != null &&
      updatedEstimate != null &&
      Boolean(estimate.isPrivatePay) !== Boolean(updatedEstimate.isPrivatePay);
    const shouldSyncLocs = Boolean(
      estimate?.facilityLevelOfCare?.length &&
      payorResult != null &&
      (!skipBecauseVobOverriddenMatchesRedux || privatePayToggled || hasIncompatibleLocs || changingFromFacility) &&
      levelsOfCareResponse != null &&
      !levelsOfCareResponse.hasErrors &&
      Array.isArray(levelsOfCareResponse.entity)
    );
    let didSyncLocs = false;
    if (shouldSyncLocs && estimate) {
      const effectiveUpdatedEstimate = updatedEstimate ?? estimate;
      await handleLOCMatching({
          estimate,
          rateAsPayorPlan,
          payorResult,
          levelsOfCareResponse,
          selectedVobId,
          client,
          facility,
          dispatch,
          updatedEstimate: effectiveUpdatedEstimate
        });
      didSyncLocs = true;
    } else if (changingFromFacility && !payorResult && estimate) {
      // Persist with empty LOCs and omit rateAsPayorPlan. Null fails Joi; omit is allowed when LOCs are empty.
      await clearEstimateLocsWhenRateAsUnresolved({
        estimate,
        updatedEstimate: updatedEstimate ?? estimate,
        selectedVobId,
        client,
        facility,
        dispatch,
      });
      didSyncLocs = true;
    }

    return didSyncLocs;

  } catch (error) {
    console.error('Error finding rateAsPayorPlan:', error);
    return false;
  }
};

// Persist facility change with empty LOCs and omit rateAsPayorPlan (do not send null).
// Joi rejects null/non-object Rate as; omit is allowed when LOCs are empty.
async function clearEstimateLocsWhenRateAsUnresolved({
  estimate,
  updatedEstimate,
  selectedVobId,
  client,
  facility,
  dispatch,
}: {
  estimate: Estimate;
  updatedEstimate: Estimate;
  selectedVobId: string | number | undefined;
  client: Client;
  facility: Facility;
  dispatch: AppDispatch;
}): Promise<void> {
  const effectiveUpdatedEstimate = updatedEstimate ?? estimate;
  const {
    estimateId,
    createUserId,
    createDt,
    lastUpdateUserId,
    lastUpdateDt,
    summary,
    crossOverSummary,
    priorCareSummary,
    rateAsPayorPlan: _omitRateAs,
    ...rest
  } = effectiveUpdatedEstimate as Estimate & { rateAsPayorPlan?: PayorPlan | null };

  const putEstimateBody: Estimate = {
    ...rest,
    patientId: effectiveUpdatedEstimate.patientId!,
    vobId: selectedVobId != null ? Number(selectedVobId) : effectiveUpdatedEstimate.vobId,
    anticipatedAdmitDate: effectiveUpdatedEstimate.anticipatedAdmitDate,
    description: effectiveUpdatedEstimate.description,
    isPlanYearCrossover: effectiveUpdatedEstimate.isPlanYearCrossover,
    isSelfPay: effectiveUpdatedEstimate.isSelfPay,
    isPrivatePay: effectiveUpdatedEstimate.isPrivatePay,
    ...(facility?.facilityId != null ? { facilityId: facility.facilityId } : {}),
    ...(client?.clientId != null ? { clientId: client.clientId } : {}),
    facilityLevelOfCare: [],
  };
  if (putEstimateBody.description === '') {
    delete putEstimateBody.description;
  }
  if (putEstimateBody.rateAsPayorPlan == null) {
    delete putEstimateBody.rateAsPayorPlan;
  }

  const saveEstimate =
    effectiveUpdatedEstimate.isSelfPay || effectiveUpdatedEstimate?.isPrivatePay
      ? saveSelectedEstimateSelfPay({ estimateId: estimate.estimateId, estimate: putEstimateBody })
      : saveSelectedEstimate({ estimateId: estimate.estimateId, estimate: putEstimateBody });

  await dispatch(saveEstimate);
  await dispatch(
    loadSavedEstimatesListv3({
      paramId: -2,
      patientId: Number(effectiveUpdatedEstimate.patientId!),
    })
  );
}

// ---- LOC Matching helper used by LoadAndSetPayorPlan----
async function handleLOCMatching({
  estimate,
  rateAsPayorPlan,
  payorResult,
  levelsOfCareResponse,
  selectedVobId,
  client,
  facility,
  dispatch,
  updatedEstimate,
}: {
  estimate: Estimate;
  rateAsPayorPlan: any | null;
  payorResult: any | null;
  levelsOfCareResponse: any;
  selectedVobId:  string | number | undefined,
  client: Client;
  facility: Facility;
  dispatch: AppDispatch;
  updatedEstimate: any;
}): Promise<{ matchedLOCs: any[]; missingLOCs: any[] }> {
  const matchedLOCs: any[] = [];
  const missingLOCs: any[] = [];
  const effectiveUpdatedEstimate = updatedEstimate ?? estimate;

  if (Array.isArray(estimate?.facilityLevelOfCare) && estimate.facilityLevelOfCare.length) {
    const levels = Array.isArray(levelsOfCareResponse?.entity) ? levelsOfCareResponse!.entity : [];  

    for (const loc of estimate?.facilityLevelOfCare ?? []) {
      const found = levels.find((lvl: any) => lvl.cfgLevelOfCareId === loc.cfgLevelOfCareId);
    
      if (!found) {
        missingLOCs.push(loc);
        continue;
      }
    
      let merged = loc;
    
      switch (effectiveUpdatedEstimate.quoteMethod) {
        case QuoteMethod.SCA: {
          merged = { ...found };
          break;
        }
      
        case QuoteMethod.MANUAL: {
          merged = {
            ...found,
            pdrRate: loc.pdrRate,
            losDays: loc.losDays,
          };
          break;
        }
      
        case QuoteMethod.AVGLOS: {
          merged = {
            ...found,
            losDays: found.losDays,
            pdrRate: found.pdrRate,
          };
          break;
        }
      
        case QuoteMethod.RLOS: {
          merged = {
            ...found,
            losDays: found.recommendedLos,
            pdrRate: found.pdrRate,
          };
          break;
        }
      
        default: {
          // Fallback: keep found as truth source
          merged = { ...found };
          break;
        }

      }
      if (estimate.isPlanYearCrossover && merged) {
        const locWithCrossover = loc as FacilityLevelOfCareWithCrossoverDays;
        merged = {
          ...merged,
          ...(locWithCrossover.losDaysAfterCrossover !== undefined && { losDaysAfterCrossover: locWithCrossover.losDaysAfterCrossover }),
          ...(locWithCrossover.losDaysBeforeCrossover !== undefined && { losDaysBeforeCrossover: locWithCrossover.losDaysBeforeCrossover }),
        };
      }
      matchedLOCs.push(merged);
    }

    // check to see if quote method is Single 
    if (
      effectiveUpdatedEstimate.quoteMethod === QuoteMethod.SCA ||
      effectiveUpdatedEstimate.quoteMethod === QuoteMethod.MANUAL
    ) {
      // Build quick lookup from old estimate LOCs by cfgLevelOfCareId
      const prevById = new Map<number, any>(
        estimate.facilityLevelOfCare.map((loc: any) => [loc.cfgLevelOfCareId, loc])
      );
    
      const isSCA = effectiveUpdatedEstimate.quoteMethod === QuoteMethod.SCA;
      const isManual = effectiveUpdatedEstimate.quoteMethod === QuoteMethod.MANUAL;

      for (const lvl of matchedLOCs) {
        const prev = prevById.get(lvl.cfgLevelOfCareId);
        if (!prev) continue;
    
        // SCA: copy rate
        if (isSCA && prev.pdrRate !== undefined) {
          lvl.pdrRate = prev.pdrRate;
        }
    
        // SCA or MANUAL: copy days
        if ((isSCA || isManual) && prev.losDays !== undefined) {
          lvl.losDays = prev.losDays;
        }
      }
    }

    // Only persist if we actually have something to set
    // if (matchedLOCs.length > 0) {
    const {
      estimateId,
      createUserId,
      createDt,
      lastUpdateUserId,
      lastUpdateDt,
      summary,
      crossOverSummary,
      priorCareSummary,
      // rateAsPayorPlan,
      ...rest
    } = effectiveUpdatedEstimate;

    const putEstimateBody: Estimate = {
      ...rest,
      patientId: effectiveUpdatedEstimate.patientId!,
      vobId: selectedVobId,
      anticipatedAdmitDate: effectiveUpdatedEstimate.anticipatedAdmitDate,
      description: effectiveUpdatedEstimate.description,
      isPlanYearCrossover: effectiveUpdatedEstimate.isPlanYearCrossover,
      isSelfPay: effectiveUpdatedEstimate.isSelfPay,
      isPrivatePay: effectiveUpdatedEstimate.isPrivatePay,
      ...(facility?.facilityId != null ? { facilityId: facility.facilityId } : {}),
      ...(client?.clientId != null ? { clientId: client.clientId } : {}),
      facilityLevelOfCare: matchedLOCs.length > 0 ? matchedLOCs : [],
      ...(payorResult && { rateAsPayorPlan: payorResult })
    };
    if (putEstimateBody.rateAsPayorPlan == null) {
      delete putEstimateBody.rateAsPayorPlan;
    }

    // Rematch Prior Care LOCs only when they belong to the same facility as the estimate.
    // Otherwise keep the original loc IDs so Edit Prior Care can still select rows and show days inputs.
    // When Rate as plan changes, days follow Quote Method (same as Levels of Care). If the plan did not
    // change, keep the user's existing days so they are not reset to the payer default.
    const priorCareLoc = estimate?.priorCare?.loc;
    const priorCareFacilityId = estimate?.priorCare?.facilityId;
    const estimateFacilityId = facility?.facilityId ?? effectiveUpdatedEstimate.facilityId ?? estimate.facilityId;
    const priorCareIsSameFacility =
      priorCareFacilityId != null &&
      estimateFacilityId != null &&
      Number(priorCareFacilityId) === Number(estimateFacilityId);

    if (priorCareIsSameFacility && Array.isArray(priorCareLoc) && priorCareLoc.length > 0 && levels.length > 0) {
      const previousFacilityPlanId =
        (estimate.rateAsPayorPlan as PayorPlan | undefined)?.plan?.facilityPlanId ?? null;
      const newFacilityPlanId = payorResult?.plan?.facilityPlanId ?? null;
      const matchedPriorCareLocs: any[] = [];
      for (const loc of priorCareLoc) {
        const found = levels.find((lvl: any) => lvl.cfgLevelOfCareId === loc.cfgLevelOfCareId);
        if (found) {
          matchedPriorCareLocs.push({
            ...found,
            losDays: resolvePriorCareLosDaysAfterRematch({
              loc,
              found,
              quoteMethod: effectiveUpdatedEstimate.quoteMethod,
              previousFacilityPlanId,
              newFacilityPlanId,
            }),
            pdrRate: found.pdrRate,
          });
        } else {
          matchedPriorCareLocs.push(loc);
        }
      }
      putEstimateBody.priorCare = {
        ...estimate.priorCare!,
        loc: matchedPriorCareLocs,
      };
    }

    const saveEstimate =
    effectiveUpdatedEstimate.isSelfPay || effectiveUpdatedEstimate?.isPrivatePay
        ? saveSelectedEstimateSelfPay({ estimateId: estimate.estimateId, estimate: putEstimateBody })
        : saveSelectedEstimate({ estimateId: estimate.estimateId, estimate: putEstimateBody });

    await dispatch(saveEstimate);
    await dispatch(
        loadSavedEstimatesListv3({
            paramId: -2,
            patientId: Number(effectiveUpdatedEstimate.patientId!),
        })
    );

    if (missingLOCs.length > 0) {
      const missingNames = missingLOCs
        .map(loc => loc.facilityLevelOfCareName || loc.facilityPayorName || 'Unknown LOC')
        .join(', ');
    
      const message = `The following Levels of Care could not be matched and will be removed from the Estimate: ${missingNames}`;
    
      dispatch(showErrorStatus(message));
    }
  }

  return { matchedLOCs, missingLOCs };
}


/*
 matches the vob payor/plan to one of the facilities payor/plans
 */
export const findMatchedPayorPlan = (
  facilityPayors: FacilityPayer[],
  vobPayor: Payor,
  vobPlan: Plan,
  payorMap: RateAsPayorMap[],
  masterPayors: Map<number, PayerRow>
): PayorPlan | null => {
  if (!facilityPayors || !vobPayor?.payorId || !vobPlan?.payorPlanId) {
    return null;
  }

  let matchedFacilityPayor = facilityPayors.find(
    (facilityPayer: FacilityPayer) =>
      facilityPayer.cfgPayorId === vobPayor.payorId
  );

  // now match plan by id
  if (matchedFacilityPayor) {
    console.info(`Matched facility payor by id: ${JSON.stringify(matchedFacilityPayor)}`);
    const matchedFacilityPlan = matchedFacilityPayor.facilityPayorPlans?.find(
        (facilityPayer: FacilityPayorPlan) =>
            facilityPayer.cfgPayorPlanId === vobPlan.payorPlanId
    );

    if (matchedFacilityPlan) {
      return {
        payor: {
          payorId: matchedFacilityPayor.cfgPayorId,
          facilityPayorId: matchedFacilityPayor.facilityPayorId,
          payorName: masterPayors.get(matchedFacilityPayor.cfgPayorId)?.payorName,
          facilityPayorName: matchedFacilityPayor.facilityPayorName,
          inNetwork: matchedFacilityPayor.inNetwork
        },
        plan: {
          payorPlanId: matchedFacilityPlan.cfgPayorPlanId,
          facilityPlanId: matchedFacilityPlan.facilityPayorPlanId,
          planName: masterPayors.get(matchedFacilityPayor.cfgPayorId)?.payorPlans?.find(p => p.payorPlanId === matchedFacilityPlan.cfgPayorPlanId)?.planName,
          facilityPlanName: matchedFacilityPlan.facilityPayorPlanName
        },
        isOverridden: false
      };
    }
    return null;
  }

  console.info(`matching by name`);
  if (!matchedFacilityPayor && payorMap && masterPayors) {
      // use the rateAsPayorMap to match the vob payor to a facilityPayor.cfgPayorId
      const vobPayorName = masterPayors.get(vobPayor.payorId)?.payorName;
      console.info(`Matching payor by master name: ${vobPayorName}, masterPayors: ${JSON.stringify(masterPayors)}`);
      if (!vobPayorName) return null;

      // match exact payor name
      let matchedPayorMap = payorMap.find((t: RateAsPayorMap) => vobPayorName.toLowerCase() === t.payorName);
      if (matchedPayorMap) {
          console.info(`Matched payor map by exact name: ${JSON.stringify(matchedPayorMap)}`);
      }

      if (!matchedPayorMap) {
        // match payor names with wildcard
        matchedPayorMap = payorMap.find((t: RateAsPayorMap) => vobPayorName.toLowerCase().includes(t.payorName));
        if (matchedPayorMap) {
            console.info(`Matched payor map by wildcard: ${JSON.stringify(matchedPayorMap)}`);
        }
      }

      if (matchedPayorMap?.rateAsPayorPlan?.payor?.payorId) {
        matchedFacilityPayor = facilityPayors.find((t: FacilityPayer) => t.cfgPayorId === matchedPayorMap?.rateAsPayorPlan?.payor?.payorId);
      }
      // now match plan by id from matched rate as map
      if (matchedFacilityPayor && matchedPayorMap) {
        const matchedFacilityPlan = matchedFacilityPayor.facilityPayorPlans?.[0];
        if (matchedFacilityPlan) {
          return {
            payor: {
              payorId: matchedFacilityPayor.cfgPayorId,
              facilityPayorId: matchedFacilityPayor.facilityPayorId,
              payorName: masterPayors.get(matchedFacilityPayor.cfgPayorId)?.payorName,
              facilityPayorName: matchedFacilityPayor.facilityPayorName,
              inNetwork: matchedFacilityPayor.inNetwork
            },
            plan: {
              payorPlanId: matchedFacilityPlan.cfgPayorPlanId,
              facilityPlanId: matchedFacilityPlan.facilityPayorPlanId,
              planName: masterPayors.get(matchedFacilityPayor.cfgPayorId)?.payorPlans?.find(p => p.payorPlanId === matchedFacilityPlan.cfgPayorPlanId)?.planName,
              facilityPlanName: matchedFacilityPlan.facilityPayorPlanName
            },
            isOverridden: false
          };
        }
      }
  }
  return null;
};

/**
 * Resolves a facility payor/plan for LOC loading, matching estimate Rate as behavior:
 * direct/rate-as map match first, then Average Out-of-Network fallback.
 */
export const resolveFacilityPayorPlanForLoc = (
  facilityPayors: FacilityPayer[],
  vobPayor: Payor,
  vobPlan: Plan,
  payorMap: RateAsPayorMap[],
  masterPayors: Map<number, PayerRow>
): PayorPlan | null => {
  const matchedPlan = findMatchedPayorPlan(
    facilityPayors,
    vobPayor,
    vobPlan,
    payorMap,
    masterPayors
  );
  if (matchedPlan) {
    return matchedPlan;
  }

  const averageOutOfNetworkPayor = findAverageOutOfNetworkPayor(
    facilityPayors,
    masterPayors
  );
  if (!averageOutOfNetworkPayor?.facilityPayorPlans?.length) {
    return null;
  }

  const firstPlan = averageOutOfNetworkPayor.facilityPayorPlans[0];
  const matchedMasterPayor = masterPayors.get(averageOutOfNetworkPayor.cfgPayorId);
  const matchedMasterPlan = matchedMasterPayor?.payorPlans?.find(
    (p: any) => p.payorPlanId === firstPlan.cfgPayorPlanId
  );

  return {
    payor: {
      payorId: averageOutOfNetworkPayor.cfgPayorId,
      facilityPayorId: averageOutOfNetworkPayor.facilityPayorId,
      payorName: matchedMasterPayor?.payorName,
      facilityPayorName: averageOutOfNetworkPayor.facilityPayorName,
      inNetwork: averageOutOfNetworkPayor.inNetwork,
    },
    plan: {
      payorPlanId: firstPlan.cfgPayorPlanId,
      facilityPlanId: firstPlan.facilityPayorPlanId,
      planName: matchedMasterPlan?.planName,
      facilityPlanName: firstPlan.facilityPayorPlanName,
    },
    isOverridden: false,
  };
};

/**
 * Finds the Average Out-of-Network payor from the facility payers list using master payors map
 * 
 * @param facilityPayers List of facility payers
 * @param masterPayors Map of master payors
 * @returns The Average Out-of-Network facility payor or undefined if not found
 */
const findAverageOutOfNetworkPayor = (
  facilityPayers: FacilityPayer[], 
  masterPayors: Map<number, PayerRow>
): FacilityPayer | undefined => {
  if (!facilityPayers || !Array.isArray(facilityPayers) || !masterPayors) {
    return undefined;
  }
  
  // First find the master payor with name "Average Out-of-Network"
  // Convert Map to array to use find
  const averageOutOfNetworkEntry = Array.from(masterPayors.entries())
    .find(([_, payorData]) => payorData.payorName === "Average Out-of-Network");
    
  const averageOutOfNetworkMasterPayorId = averageOutOfNetworkEntry ? averageOutOfNetworkEntry[0] : undefined;
  
  // If we found a master payor, find the matching facility payor by cfgPayorId
  if (averageOutOfNetworkMasterPayorId) {
    const matchByMasterId = facilityPayers.find(
      (fp: FacilityPayer) => fp.cfgPayorId === averageOutOfNetworkMasterPayorId
    );
    
    if (matchByMasterId) {
      return matchByMasterId;
    }
  }
  
  // Fallback to name matching if no match by ID
  // try case-insensitive match
  const caseInsensitiveMatch = facilityPayers.find(
    (fp: FacilityPayer) => 
      fp.facilityPayorName && 
      fp.facilityPayorName.toLowerCase().includes("average out-of-network")
  );
  
  return caseInsensitiveMatch;
};
